import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { Camera, Star, Instagram } from "lucide-react";
import AnimatedCounter from "./AnimatedCounter";
import { fetchTeam, resolveAssetUrl } from "../lib/api";
import { KEANU_PORTRAIT } from "../data/mock";

const TIKTOK_ICON = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.1z" />
  </svg>
);

// Fallback used when API is empty / unreachable
const FALLBACK_FOUNDER = {
  id: "fallback",
  name: "Keanu Irle",
  role: "Social Media Strategist",
  role_label: "Founder",
  bio:
    "Keanu Irle, auch bekannt als UGC Creator und Videograf, hat bereits für einige der größten Brands Deutschlands Content produziert. Von Porsche über Mercedes bis hin zu Five Guys, Subway und Salitos – Keanu versteht es, Content zu kreieren, der nicht nur gut aussieht, sondern auch Millionen Views erzielt.\n\n2024 wurde er als Top UGC Creator (Germany) ausgezeichnet und war auf Speekly ausschließlich 5-Star rated.",
  portrait: KEANU_PORTRAIT,
  is_founder: true,
  badges: [
    { icon: "Award", text: "Top UGC Creator 2024" },
    { icon: "Star", text: "5-Star auf Speekly" },
    { icon: "Camera", text: "4M+ Views generiert" },
  ],
};

const FounderHero = ({ member }) => (
  <div className="grid lg:grid-cols-12 gap-12 items-center">
    <div className="lg:col-span-5">
      <div className="relative aspect-[4/5] rounded-3xl overflow-hidden bg-neutral-950 group">
        <img
          src={resolveAssetUrl(member.portrait)}
          alt={`${member.name} – ${member.role_label} FREEAYTO`}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" />
        <div className="absolute inset-0 flex items-end p-8">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-white/70 font-semibold">
              {member.role_label}
            </div>
            <div className="mt-2 font-display font-extrabold text-4xl md:text-5xl text-white tracking-tight">
              {member.name}
            </div>
            {member.role && (
              <div className="mt-1 bg-gradient-to-r from-pink-300 via-orange-300 to-purple-300 bg-clip-text text-transparent font-bold">
                {member.role}
              </div>
            )}
          </div>
        </div>
        {member.badges?.[0]?.text && (
          <div className="absolute top-6 right-6 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur border border-white/20 text-white text-xs font-semibold flex items-center gap-1.5">
            <Star size={12} className="fill-amber-300 text-amber-300" />
            {member.badges[0].text}
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3 mt-4">
        <div className="p-4 rounded-2xl bg-neutral-50 border border-black/5">
          <AnimatedCounter
            value={8700000}
            format="compact"
            decimals={1}
            suffix="+"
            className="font-display font-extrabold text-2xl text-neutral-950"
          />
          <div className="text-xs text-neutral-500 mt-1">Total Views</div>
        </div>
        <div className="p-4 rounded-2xl bg-neutral-50 border border-black/5">
          <AnimatedCounter
            value={78}
            suffix="+"
            className="font-display font-extrabold text-2xl text-neutral-950"
          />
          <div className="text-xs text-neutral-500 mt-1">Top Brands</div>
        </div>
        <div className="p-4 rounded-2xl bg-neutral-50 border border-black/5">
          <div className="font-display font-extrabold text-2xl text-neutral-950">5★</div>
          <div className="text-xs text-neutral-500 mt-1">Speekly</div>
        </div>
      </div>
    </div>

    <div className="lg:col-span-7">
      <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-neutral-100 border border-black/5 text-xs uppercase tracking-[0.2em] font-semibold text-neutral-700">
        <Camera size={14} className="text-pink-500" />
        UGC Creator von Top-Brands
      </span>
      <h2 className="font-display font-extrabold text-4xl md:text-6xl lg:text-7xl tracking-[-0.03em] leading-[1.02] mt-6 text-neutral-950">
        {member.name}{" "}
        <span className="bg-gradient-to-r from-pink-500 via-orange-500 to-purple-500 bg-clip-text text-transparent">
          stellt sich vor.
        </span>
      </h2>
      {member.bio && (
        <div className="mt-6 space-y-4">
          {member.bio.split(/\n\n+/).map((p, i) => (
            <p key={i} className="text-lg text-neutral-700 leading-relaxed whitespace-pre-line">
              {p}
            </p>
          ))}
        </div>
      )}

      {member.badges?.length > 0 && (
        <div className="mt-8 flex flex-wrap gap-3">
          {member.badges.map((b, i) => {
            const Ico = Icons[b.icon] || Star;
            return (
              <div
                key={i}
                className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-neutral-50 border border-black/5 text-sm font-medium text-neutral-800"
              >
                <Ico size={14} className="text-pink-500" />
                {b.text}
              </div>
            );
          })}
        </div>
      )}

      {(member.instagram || member.tiktok) && (
        <div className="mt-6 flex items-center gap-3">
          {member.instagram && (
            <a
              href={member.instagram}
              target="_blank"
              rel="noreferrer"
              className="w-10 h-10 rounded-full bg-neutral-100 hover:bg-gradient-to-br hover:from-pink-500 hover:via-orange-500 hover:to-purple-500 flex items-center justify-center hover:text-white transition-all"
            >
              <Instagram size={16} />
            </a>
          )}
          {member.tiktok && (
            <a
              href={member.tiktok}
              target="_blank"
              rel="noreferrer"
              className="w-10 h-10 rounded-full bg-neutral-100 hover:bg-neutral-950 flex items-center justify-center hover:text-white transition-all"
            >
              {TIKTOK_ICON}
            </a>
          )}
        </div>
      )}
    </div>
  </div>
);

const MemberCard = ({ member }) => (
  <div className="group relative rounded-3xl overflow-hidden bg-neutral-50 border border-black/5 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_30px_60px_-20px_rgba(0,0,0,0.18)]">
    <div className="relative aspect-[4/5] bg-neutral-200 overflow-hidden">
      {member.portrait ? (
        <img
          src={resolveAssetUrl(member.portrait)}
          alt={member.name}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
          loading="lazy"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-pink-500/20 via-orange-500/20 to-purple-500/20" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/80">
          {member.role_label}
        </div>
        <div className="mt-1 font-display font-extrabold text-2xl tracking-tight">
          {member.name}
        </div>
        {member.role && (
          <div className="text-sm font-semibold text-white/90">{member.role}</div>
        )}
      </div>
    </div>

    {(member.bio || member.badges?.length || member.instagram || member.tiktok) && (
      <div className="p-5">
        {member.bio && (
          <p className="text-sm text-neutral-700 leading-relaxed line-clamp-4 whitespace-pre-line">
            {member.bio}
          </p>
        )}
        {member.badges?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {member.badges.map((b, i) => {
              const Ico = Icons[b.icon] || Star;
              return (
                <div
                  key={i}
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white border border-black/5 text-[11px] font-semibold text-neutral-700"
                >
                  <Ico size={11} className="text-pink-500" />
                  {b.text}
                </div>
              );
            })}
          </div>
        )}
        {(member.instagram || member.tiktok) && (
          <div className="mt-4 flex items-center gap-2">
            {member.instagram && (
              <a
                href={member.instagram}
                target="_blank"
                rel="noreferrer"
                className="w-8 h-8 rounded-full bg-neutral-100 hover:bg-gradient-to-br hover:from-pink-500 hover:via-orange-500 hover:to-purple-500 flex items-center justify-center hover:text-white transition-all"
              >
                <Instagram size={14} />
              </a>
            )}
            {member.tiktok && (
              <a
                href={member.tiktok}
                target="_blank"
                rel="noreferrer"
                className="w-8 h-8 rounded-full bg-neutral-100 hover:bg-neutral-950 flex items-center justify-center hover:text-white transition-all"
              >
                {TIKTOK_ICON}
              </a>
            )}
          </div>
        )}
      </div>
    )}
  </div>
);

const TeamSection = () => {
  const [members, setMembers] = useState(null);

  useEffect(() => {
    fetchTeam()
      .then((d) => setMembers(d.items || []))
      .catch(() => setMembers([FALLBACK_FOUNDER]));
  }, []);

  const list = members && members.length > 0 ? members : [FALLBACK_FOUNDER];
  const founder = list.find((m) => m.is_founder) || list[0];
  const others = list.filter((m) => m.id !== founder.id);

  return (
    <section
      id="team"
      className="py-24 md:py-36 bg-white relative overflow-hidden"
    >
      <div className="max-w-[1300px] mx-auto px-6 md:px-10">
        <FounderHero member={founder} />

        {others.length > 0 && (
          <div className="mt-24 md:mt-32">
            <div className="max-w-2xl mb-12">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-neutral-100 border border-black/5 text-xs uppercase tracking-[0.2em] font-semibold text-neutral-700">
                Das Team
              </span>
              <h3 className="font-display font-extrabold text-3xl md:text-5xl tracking-[-0.03em] leading-[1.05] mt-6 text-neutral-950">
                Die Köpfe hinter{" "}
                <span className="bg-gradient-to-r from-pink-500 via-orange-500 to-purple-500 bg-clip-text text-transparent">
                  FREEAYTO.
                </span>
              </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {others.map((m) => (
                <MemberCard key={m.id} member={m} />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default TeamSection;
